import { Box, Tooltip } from '@mui/material';
import styled from 'styled-components';
import {
  CustomBarChart,
  CustomDonutChart,
  Typography,
  colors,
  IconGraphy,
  Avatar,
  styles,
} from '@leapeasy/ui-kit';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as DashboardEmptyStateIcon } from 'assets/dashboard-empty-state.svg';
import { navigateApplicationsFromDashboardFilter } from 'utils/navigateApplicationsFromDashboardFilter';
import { getPoliciesPathForDashboardFilter } from 'utils/policyDashboardNavigation';

const POLICY_ISSUED_FILTER_KEY = 'Policy Issued - Stage 05';

const KPI_COLORS = {
  total: '#4840E9',
  past: '#64209A',
  current: '#46C891',
  future: '#FFC452',
};

const StatBlock = ({ label, count, tooltipTitle, avatarBg, color, onActivate }) => (
  <Box
    display="flex"
    gap="8px"
    alignItems="center"
    sx={onActivate ? { cursor: 'pointer', '&:hover': { opacity: 0.88 } } : undefined}
    onClick={onActivate}
    role={onActivate ? 'button' : undefined}
    tabIndex={onActivate ? 0 : undefined}
    onKeyDown={
      onActivate
        ? (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onActivate();
            }
          }
        : undefined
    }
  >
    <Avatar
      size="medium"
      iconImage={<IconGraphy icon="Security.Policy" />}
      bgColor={avatarBg}
      color={color}
    />
    <Box>
      <Typography variant="body3" style={{ color: colors.black[900], fontWeight: '600' }}>
        {Number(count ?? 0).toLocaleString()}
      </Typography>
      <Tooltip title={tooltipTitle} placement="top" arrow enterDelay={200}>
        <Typography variant="small" style={{ color: colors.neutral[900], fontWeight: '500' }}>
          {label}
        </Typography>
      </Tooltip>
    </Box>
  </Box>
);

export const PolicyIssuedChart = ({ data, onPolicyIssuedDrilldown, onLeaseBucketDrilldown }) => {
  const navigate = useNavigate();

  const stage05 = Number(data?.stage05 ?? 0);
  const pastCount = Number(data?.pastCount ?? 0);
  const currentCount = Number(data?.currentCount ?? 0);
  const futureCount = Number(data?.futureCount ?? 0);
  const leaseSplitSum = pastCount + currentCount + futureCount;
  const barRows = Array.isArray(data?.appChartData) ? data.appChartData : [];

  const goPolicyIssued = () => {
    if (onPolicyIssuedDrilldown) {
      onPolicyIssuedDrilldown();
    } else {
      navigateApplicationsFromDashboardFilter(navigate, POLICY_ISSUED_FILTER_KEY);
    }
  };

  const goLeaseBucket = (bucketKey) => {
    if (onLeaseBucketDrilldown) {
      onLeaseBucketDrilldown(bucketKey);
      return;
    }
    navigate(getPoliciesPathForDashboardFilter(bucketKey));
  };

  if (stage05 <= 0 && leaseSplitSum <= 0 && barRows.length === 0) {
    return (
      <EmptyStateContainer
        boxShadow={styles.boxShadow.sm}
        borderRadius={`${styles.borderRadius.large}px`}
        border={`1px solid ${colors.black[300]}`}
      >
        <Box display="flex" gap="16px" alignItems="center">
          <Avatar
            size="large"
            iconImage={
              <IconGraphy icon="Security.Policy" style={{ width: '24px', height: '24px' }} />
            }
            bgColor={colors.purple[25]}
            color={colors.purple[600]}
          />
          <Box>
            <Typography variant="h4" style={{ color: colors.purple[900], fontWeight: '400' }}>
              Policies Issued
            </Typography>
            <Typography variant="body3" style={{ color: colors.neutral[900], fontWeight: '400' }}>
              Past, current, and future issued policies by month (matches production dashboard).
            </Typography>
          </Box>
        </Box>
        <EmptyBody>
          <DashboardEmptyStateIcon />
          <Typography
            variant="body2"
            style={{
              textAlign: 'center',
              color: colors.neutral[900],
              maxWidth: '360px',
              fontWeight: '500',
            }}
          >
            There&apos;s not any policy-issued data to show for this scope yet.
          </Typography>
        </EmptyBody>
      </EmptyStateContainer>
    );
  }

  const donutData =
    leaseSplitSum > 0
      ? [
          { name: 'Past', value: (pastCount / leaseSplitSum) * 100 },
          { name: 'Current', value: (currentCount / leaseSplitSum) * 100 },
          { name: 'Future', value: (futureCount / leaseSplitSum) * 100 },
        ]
      : [
          { name: 'Past', value: 0 },
          { name: 'Current', value: 0 },
          { name: 'Future', value: 0 },
        ];

  return (
    <PolicyIssuedContainer
      boxShadow={styles.boxShadow.sm}
      borderRadius={`${styles.borderRadius.large}px`}
      border={`1px solid ${colors.black[300]}`}
    >
      <Box display="flex" gap="16px" alignItems="center">
        <Avatar size="large" iconImage={<IconGraphy icon="Security.Policy" />} />
        <Box>
          <Typography variant="h4" style={{ color: colors.purple[900], fontWeight: '400' }}>
            Policies Issued
          </Typography>
          <Typography variant="body3" style={{ color: colors.neutral[900], fontWeight: '400' }}>
            Total issued policies and lease timing split (past / current / future), by month.
          </Typography>
        </Box>
      </Box>

      <InnerPanel>
        <Box display="flex" flexDirection="column" gap="20px" flex="1.6 1 0%" minWidth={0}>
          <Box
            display="flex"
            flexWrap="wrap"
            gap="20px"
            justifyContent="space-between"
            alignItems="center"
          >
            <StatBlock
              label="Policies Issued"
              count={stage05}
              tooltipTitle="All applications at Policy Issued"
              avatarBg="#ECECFD"
              color={KPI_COLORS.total}
              onActivate={goPolicyIssued}
            />
            <StatBlock
              label="Past"
              count={pastCount}
              tooltipTitle="Lease end date is before today"
              avatarBg="#EFE8F5"
              color={KPI_COLORS.past}
              onActivate={() => goLeaseBucket('policy_issued_past')}
            />
            <StatBlock
              label="Current"
              count={currentCount}
              tooltipTitle="Active within lease window"
              avatarBg="#ECF9F4"
              color={KPI_COLORS.current}
              onActivate={() => goLeaseBucket('policy_issued_current')}
            />
            <StatBlock
              label="Future"
              count={futureCount}
              tooltipTitle="Lease start date is after today"
              avatarBg="#FFF6E5"
              color={KPI_COLORS.future}
              onActivate={() => goLeaseBucket('policy_issued_future')}
            />
          </Box>

          <Box
            sx={{
              background: 'white',
              border: `1px solid ${colors.black[200]}`,
              borderRadius: `${styles.borderRadius.large}px`,
              padding: '16px',
              boxSizing: 'border-box',
            }}
          >
            <CustomBarChart
              chartWidth="100%"
              chartHeight={220}
              data={barRows}
              colors={{
                Past: KPI_COLORS.past,
                Current: KPI_COLORS.current,
                Future: KPI_COLORS.future,
              }}
            />
          </Box>
        </Box>

        <Box
          sx={{
            flex: '1 1 0%',
            minWidth: 200,
            background: 'white',
            border: `1px solid ${colors.black[200]}`,
            borderRadius: `${styles.borderRadius.large}px`,
            padding: '16px',
            boxSizing: 'border-box',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {leaseSplitSum > 0 ? (
            <CustomDonutChart
              chartWidth={200}
              chartHeight={200}
              donutMinAngle={4}
              data={donutData}
              colors={[KPI_COLORS.past, KPI_COLORS.current, KPI_COLORS.future]}
              title="Lease timing"
              description={leaseSplitSum.toLocaleString()}
            />
          ) : (
            <Box textAlign="center" px={1}>
              <Typography variant="body3" style={{ color: colors.neutral[900], fontWeight: '500' }}>
                No past / current / future split
              </Typography>
              <Typography variant="small" style={{ color: colors.neutral[700], marginTop: '8px' }}>
                Monthly bars may still reflect timing when totals are not classified.
              </Typography>
            </Box>
          )}
        </Box>
      </InnerPanel>
    </PolicyIssuedContainer>
  );
};

const InnerPanel = styled(Box)`
  display: flex;
  gap: 20px;
  align-items: stretch;
  width: 100%;
  box-sizing: border-box;
  padding: 12px;
  border-radius: 8px;
  background: #f9f8f9;
  border: 1px solid ${colors.purple[100]};

  @media screen and (max-width: ${({ theme }) => theme.lg}) {
    flex-direction: column;
  }
`;

const PolicyIssuedContainer = styled.div`
  width: 100%;
  padding: 16px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 16px;
  box-shadow: ${(props) => props.boxShadow};
  border-radius: ${(props) => props.borderRadius};
  border: ${(props) => props.border};
`;

const EmptyStateContainer = styled(Box)`
  width: 100%;
  min-height: 320px;
  padding: 16px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-shadow: ${(props) => props.boxShadow};
  border-radius: ${(props) => props.borderRadius};
  border: ${(props) => props.border};
`;

const EmptyBody = styled(Box)`
  width: 100%;
  flex: 1;
  min-height: 220px;
  background: white;
  border: 1px solid ${colors.black[200]};
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 16px;
  box-sizing: border-box;
`;
